import { BlockNoteEditor } from '@blocknote/core';
import { TextBlockSchema } from '../schema';

type TextBlockEditor = BlockNoteEditor<TextBlockSchema>;
type PmResolvedPos = ReturnType<
  TextBlockEditor['prosemirrorState']['doc']['resolve']
>;
type LinkMarkType = NonNullable<TextBlockEditor['pmSchema']['marks']['link']>;

export default function getLinkMarkRange(
  $pos: PmResolvedPos,
  linkType: LinkMarkType,
  attributes?: Record<string, unknown>,
):
  | {
      from: number;
      to: number;
    }
  | undefined {
  let start = $pos.parent.childAfter($pos.parentOffset);

  if (!start.node?.marks.some((mark) => mark.type === linkType)) {
    start = $pos.parent.childBefore($pos.parentOffset);
  }

  if (!start.node?.marks.some((mark) => mark.type === linkType)) {
    return undefined;
  }

  let attrsToMatch = attributes;
  if (!attrsToMatch) {
    const firstMark = start.node.marks.find((mark) => mark.type === linkType);
    if (firstMark) {
      attrsToMatch = { ...firstMark.attrs };
    } else {
      attrsToMatch = {};
    }
  }

  if (!findLinkMarkInSet([...start.node.marks], linkType, attrsToMatch)) {
    return undefined;
  }

  let startIndex = start.index;
  let startPos = $pos.start() + start.offset;
  let endIndex = startIndex + 1;
  let endPos = startPos + start.node.nodeSize;

  while (
    startIndex > 0 &&
    isLinkMarkInSet(
      [...$pos.parent.child(startIndex - 1).marks],
      linkType,
      attrsToMatch,
    )
  ) {
    startIndex -= 1;
    startPos -= $pos.parent.child(startIndex).nodeSize;
  }

  while (
    endIndex < $pos.parent.childCount &&
    isLinkMarkInSet(
      [...$pos.parent.child(endIndex).marks],
      linkType,
      attrsToMatch,
    )
  ) {
    endPos += $pos.parent.child(endIndex).nodeSize;
    endIndex += 1;
  }

  return {
    from: startPos,
    to: endPos,
  };
}

function isLinkMarkInSet(
  marks: ReadonlyArray<{
    type: LinkMarkType;
    attrs: Record<string, unknown>;
  }>,
  linkType: LinkMarkType,
  attributes: Record<string, unknown>,
) {
  return findLinkMarkInSet(marks, linkType, attributes) !== undefined;
}

function findLinkMarkInSet(
  marks: ReadonlyArray<{
    type: LinkMarkType;
    attrs: Record<string, unknown>;
  }>,
  linkType: LinkMarkType,
  attributes: Record<string, unknown>,
) {
  for (const item of marks) {
    if (item.type !== linkType) {
      continue;
    }
    const markSlice = Object.fromEntries(
      Object.keys(attributes).map((k) => [k, item.attrs[k]]),
    );
    if (linkMarkSubsetMatches(markSlice, attributes)) {
      return item;
    }
  }
  return undefined;
}

function linkMarkSubsetMatches(
  markSlice: Record<string, unknown>,
  subset: Record<string, unknown>,
) {
  const keys = Object.keys(subset);
  if (keys.length === 0) {
    return true;
  }
  return keys.every((key) => markSlice[key] === subset[key]);
}
